import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  HostListener,
  ChangeDetectionStrategy
} from "@angular/core";
import {
  IonButton,
  IonButtons,
  IonIcon,
  IonLabel,
  IonMenuButton,
  IonTitle,
  IonToggle,
  IonToolbar,
  NavController,
  Platform,
  PopoverController,
} from "@ionic/angular";
import { TranslatePipe } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { PopoverOptionsComponent } from "src/components/popup/popover-options/popover-options.component";
import { TreeNode } from "src/model/class/general classes/tree-node";
import { ApplicationTheme } from "src/components/shared-components/enum/enumMisc";
import { TreeGatewayTag } from "src/model/enum/enumTagGatewayTree";
import { EventService } from "src/service/event.service";
import { SystemRouterService } from "src/service/system-router.service";
import { ThemeService } from "src/service/theme.service";
import { PopoverMultipleCommandsToolbarComponent } from "src/components/popup/popover-mutiple-commands-toolbar/popover-multiple-commands-toolbar.component";
import { GenericUserData } from "src/model/interface/generic-user-data";
import { filter, map } from "rxjs/operators";
import { currentUserData } from "src/state/selectors/authentication.selectors";
import { CurrentSessionData } from "src/model/class/general classes/current-session-data";
import { Store } from "@ngrx/store";
import { HideDataService } from "src/components/shared-components/service/hide-data.service";

@Component({
    selector: "app-toolbar-dp",
    templateUrl: "./toolbar-dp.component.html",
    styleUrls: ["./toolbar-dp.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    // Standalone: the pages of the shared-components submodule list it in their own imports
    imports: [IonToolbar, IonButtons, IonMenuButton, IonButton, IonIcon, IonTitle, IonToggle, IonLabel, TranslatePipe],
})
export class ToolbarDPComponent implements OnInit, OnDestroy {
  public currentTheme = ApplicationTheme;
  @Input() showBurger: boolean = false;
  @Input() showBackButton: boolean = false;
  @Input() paramsBack: string | Array<string> = "";
  @Input() methodOnBack: string = "";
  @Input() renderingPrevScreen: any = null;

  @Input() showSettings: boolean = false;

  @Input() title: string = "";
  @Input() showToggle: boolean = false;
  @Input() toggleChecked: boolean = false;

  @Input() isSingleButtonCommand: boolean = false;
  @Input() isMultipleButtonsCommand: boolean = false;
  @Input() functionToExecuteForSingleButtonCommand: any = null;
  @Input() singleButtonLabel: string = "";
  @Input() singleButtonCommandIconName: string = "";
  /** Shows the single command dimmed (still clickable, e.g. to explain why it is not available yet). */
  @Input() singleButtonCommandMuted: boolean = false;
  /** Disables the single command (e.g. while a save is in progress). */
  @Input() singleButtonCommandDisabled: boolean = false;
  @Input() buttonsToShow: Array<{ [key: string]: any }> = [];

  @Output() onToggleSupervisionMode = new EventEmitter<any>();
  private _selectCurrentUserSub: Subscription;
  private _hiddenModeSub: Subscription;

  public showButtonToHideData: boolean = false;
  public isDataHidden: boolean = false;

  constructor(
    private _popoverController: PopoverController,
    private _routerService: SystemRouterService,
    public themeService: ThemeService,
    private _store: Store,
    private _hideDataService: HideDataService
  ) {}

  ngOnInit() {
    this.loadCurrentUserData();
    this._hiddenModeSub = this._hideDataService.isDataHidden.subscribe(
      (value) => {
        this.isDataHidden = value;
      }
    );
  }

  ngOnDestroy() {
    if (this._selectCurrentUserSub) {
      this._selectCurrentUserSub.unsubscribe();
    }
    if (this._hiddenModeSub) {
      this._hiddenModeSub.unsubscribe();
    }
  }

  /**
   * Handle click mdoe supervision view types
   */
  public onToggleSupervision(toggleViewMode: any): void {
    this.onToggleSupervisionMode.emit(toggleViewMode);
  }

  ngOnChanges() {}

  /**
   * Opent popover to select settings entry
   *
   * @param event
   * @returns
   */
  public async onDropdownMenuSetting(event: any): Promise<void> {
    //this.selectFileRestore();
    const popover = await this._popoverController.create({
      component: PopoverOptionsComponent,
      event: event,
      translucent: false,
      showBackdrop: false,
    });

    await popover.present();
  }

  /**
   * Open popover with toolbar commands
   * @param event
   */
  public async onClickOpenPopoverCommands(event: Event): Promise<void> {
    const popover = await this._popoverController.create({
      component: PopoverMultipleCommandsToolbarComponent,
      event: event,
      translucent: false,
      showBackdrop: false,
      componentProps: { buttonsToShow: this.buttonsToShow },
    });

    await popover.present();
  }

  /**
   * On click go back
   */
  public onClickBack() {
    if (this.paramsBack) {
      if (Array.isArray(this.paramsBack)) {
        this._routerService[this.methodOnBack](...this.paramsBack);
      } else {
        this._routerService[this.methodOnBack](this.paramsBack);
      }
    } else {
      this._routerService[this.methodOnBack]();
    }
  }

  /**
   * On click page with pending invitation
   */
  public onClickOpenInvitation() {
    this._routerService.navigateToDigitalPlaceInvitation();
  }

  /**
   * Toggle for hide data mode for root user
   */
  public toggleHideDataMode(): void {
    this._hideDataService.isDataHidden.next(
      !this._hideDataService.isDataHidden.value
    );
  }

  /**
   * Load current user data
   */
  private loadCurrentUserData(): void {
    this._selectCurrentUserSub = this._store
      .select(currentUserData)
      .pipe(
        filter((currentUserData: GenericUserData) => currentUserData !== null),
        map((currentUserData: GenericUserData) => currentUserData?.currentUser)
      )
      .subscribe(async (currentUser: CurrentSessionData) => {
        if (currentUser?.role === 1) {
          this.showButtonToHideData = true;
          this._hideDataService.isDataHidden.next(true);
        } else {
          this.showButtonToHideData = false;
          this._hideDataService.isDataHidden.next(false);
        }
      });
  }
}
